// Session AMT: the filter bar above the office Call-outs list (CalloutsPage.tsx). Presentational
// only -- every filter reads from and writes straight to the URL query string via the same
// searchParams/setSearchParams pair the page already owns for its own ?new=/?job= handling
// (never a second useSearchParams() call), so this bar and the rest of the page can never
// disagree about the current URL. Pure logic (parsing, the AND predicate, the sort accessors)
// lives in calloutListFilters.ts and is unit tested there; this file is rendering and event
// wiring only.
//
// Status pill accessible names carry a "Filter: " prefix (aria-label, not the visible text) so
// they never collide with the row-level lifecycle buttons that share the same words -- a Complete
// call-out's own row renders a "Complete" ConfirmButton, and without this a test (or a screen
// reader user) asking for "the Complete button" would get two.
//
// Session SC7B (the calm screens, part 2): the raised-from / raised-to date range sits behind a single "Filters"
// disclosure, so the bar at rest is the status chips, the site picker and the search. THE URL PARAMETERS ARE UNCHANGED
// (from, to, status, site, q) and every filter works exactly as before; the disclosure only decides whether the two date
// boxes are on screen. An ACTIVE date filter is never hidden: while either date is set the disclosure is open and cannot be
// closed, so the bar can never hide what is narrowing the list. "Clear all filters" stays where it was, visible whenever any
// filter is active, because it also clears the status, site and search filters that sit outside the disclosure.
import { useState } from "react";
import { Button } from "../../components/Button";
import { RecordPicker } from "../../components/RecordPicker";
import { ListToolbar } from "../../components/ListToolbar";
import { CALLOUT_STATUSES, STATUS_LABEL, activeFilterCount, type CalloutFilterState, type CalloutStatus } from "./calloutListFilters";

type SetParams = (next: URLSearchParams, opts?: { replace?: boolean }) => void;

function pillClass(active: boolean): string {
  return `rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
    active ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-green"
  }`;
}

export function CalloutsFilterBar({
  searchParams,
  setSearchParams,
  filterState,
}: {
  searchParams: URLSearchParams;
  setSearchParams: SetParams;
  filterState: CalloutFilterState;
}) {
  function write(name: string, value: string | null) {
    const next = new URLSearchParams(searchParams);
    if (value == null || value === "") next.delete(name);
    else next.set(name, value);
    setSearchParams(next, { replace: true });
  }

  function toggleStatus(status: CalloutStatus) {
    const current = filterState.allStatuses ? new Set<CalloutStatus>(CALLOUT_STATUSES) : new Set(filterState.statuses);
    if (current.has(status)) current.delete(status);
    else current.add(status);
    write("status", current.size === CALLOUT_STATUSES.length ? "all" : [...current].join(","));
  }

  function clearAll() {
    const next = new URLSearchParams(searchParams);
    for (const name of ["status", "site", "from", "to", "q"]) next.delete(name);
    setSearchParams(next, { replace: true });
  }

  const count = activeFilterCount(filterState);
  const dateCount = (filterState.from ? 1 : 0) + (filterState.to ? 1 : 0);
  const [datesOpen, setDatesOpen] = useState(false);
  const showDates = datesOpen || dateCount > 0;

  return (
    <div className="mb-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {CALLOUT_STATUSES.map((s) => {
          const active = filterState.allStatuses || filterState.statuses.has(s);
          return (
            <button
              key={s}
              type="button"
              aria-pressed={active}
              aria-label={`Filter: ${STATUS_LABEL[s]}`}
              onClick={() => toggleStatus(s)}
              className={pillClass(active)}
            >
              {STATUS_LABEL[s]}
            </button>
          );
        })}
        <span className="mx-1 h-5 w-px bg-ele-purple-accent" aria-hidden />
        <button
          type="button"
          aria-pressed={filterState.allStatuses}
          aria-label="Filter: All statuses"
          onClick={() => write("status", "all")}
          className={pillClass(filterState.allStatuses)}
        >
          All statuses
        </button>
      </div>

      {filterState.statusIsDefault && (
        <p className="text-xs text-ele-body opacity-80">
          Showing outstanding call-outs only (not completed or cancelled).{" "}
          <button type="button" onClick={() => write("status", "all")} className="text-ele-blue hover:underline">
            Show all
          </button>
        </p>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-56">
          {/* Session SC2C: the site is SEARCHED on the server (the shared picker), not read from the loaded rows. */}
          <RecordPicker
            kind="site"
            label="Filter by site"
            placeholder="Any site"
            value={filterState.siteKey ?? ""}
            onChange={(id) => write("site", id || null)}
          />
        </div>

        <button
          type="button"
          aria-expanded={showDates}
          aria-controls="callout-filter-dates"
          onClick={() => setDatesOpen((o) => !o)}
          className={pillClass(showDates)}
        >
          Filters{dateCount > 0 ? ` (${dateCount})` : ""}
        </button>
      </div>

      {showDates && (
        <div id="callout-filter-dates" className="flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="callout-filter-from" className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">
              Raised from
            </label>
            <input
              id="callout-filter-from"
              type="date"
              value={filterState.from ?? ""}
              onChange={(e) => write("from", e.target.value || null)}
              className="rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-1.5 text-sm outline-none focus:border-ele-green"
            />
          </div>
          <div>
            <label htmlFor="callout-filter-to" className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">
              Raised to
            </label>
            <input
              id="callout-filter-to"
              type="date"
              value={filterState.to ?? ""}
              onChange={(e) => write("to", e.target.value || null)}
              className="rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-1.5 text-sm outline-none focus:border-ele-green"
            />
          </div>
        </div>
      )}

      {/* The search is the shared ListToolbar (design system Session 4), keeping its `q` URL key and
          its placeholder; the count and the "Clear all filters" control sit in its filters slot. */}
      <ListToolbar
        search={filterState.q}
        onSearchChange={(v) => write("q", v || null)}
        searchPlaceholder="Reference, customer or fault"
        filters={
          count > 0 ? (
            <Button variant="ghost" size="sm" onClick={clearAll}>
              Clear all filters ({count})
            </Button>
          ) : undefined
        }
      />
    </div>
  );
}
